import React, { useState, useEffect, useCallback } from "react";
import { ref, get } from "firebase/database";
import { db } from "./firebase/config";
import "./AdminUserManagement.css"; // Reuse existing CSS

const AdminManusKeyManager = () => {
  const [loading, setLoading] = useState(true);
  const [envKeys, setEnvKeys] = useState([]);
  const [keyRequestCounts, setKeyRequestCounts] = useState({});

  // Toast state
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(
      () => setToast({ show: false, message: "", type: "success" }),
      3000
    );
  };

  // Get all Manus API keys from environment variables
  const getEnvKeys = useCallback(() => {
    const keys = [];

    // Primary key: REACT_APP_MANUS_API_KEY
    const primaryKey = process.env.REACT_APP_MANUS_API_KEY;
    keys.push({
      id: "REACT_APP_MANUS_API_KEY",
      key: primaryKey || "",
      type: "primary",
      envVar: "REACT_APP_MANUS_API_KEY",
      description: "Primary Manus API Key",
      isEmpty: !primaryKey,
    });

    // Optional keys: MANUS_API_KEY_1 to MANUS_API_KEY_20 (không có tiền tố REACT_APP)
    // Note: Các key này chỉ available trong server-side (Vercel functions)
    // Trong browser, chúng sẽ là undefined/empty
    for (let index = 1; index <= 20; index++) {
      const envVarName = `MANUS_API_KEY_${index}`;
      const keyValue = process.env[envVarName];

      keys.push({
        id: envVarName,
        key: keyValue || "",
        type: "optional",
        envVar: envVarName,
        description: `Optional Manus API Key ${index}`,
        isEmpty: !keyValue,
      });
    }

    return keys;
  }, []);

  // Count requests per key
  const countRequestsPerKey = useCallback(async (keys) => {
    try {
      const counts = {};

      // Initialize all keys with 0
      keys.forEach((key) => {
        counts[key.id] = 0;
      });

      // Get all histories from all users
      const historiesRef = ref(db, "histories");
      const snapshot = await get(historiesRef);

      if (snapshot.exists()) {
        const allHistories = snapshot.val();

        // Count requests for each key
        Object.values(allHistories).forEach((userHistories) => {
          if (userHistories && typeof userHistories === "object") {
            Object.values(userHistories).forEach((history) => {
              // Count all requests with manus_task_id
              if (history.manus_task_id) {
                // If manus_key_id is set, use it; otherwise increment primary key
                let keyId = history.manus_key_id || "REACT_APP_MANUS_API_KEY";

                // ✅ Map Firebase key IDs to env var names
                // Firebase lưu key IDs với format khác (e.g., env_key, -NXxx...)
                // Cần map về format env var name để match
                if (keyId === "env_key") {
                  keyId = "REACT_APP_MANUS_API_KEY";
                }

                // Nếu keyId không match với bất kỳ key nào trong danh sách,
                // có thể đây là key đã bị xóa hoặc key từ hệ thống cũ
                if (counts.hasOwnProperty(keyId)) {
                  counts[keyId]++;
                } else {
                  // Log để debug (optional)
                  console.debug(
                    "Unknown key ID in history:",
                    keyId,
                    "- skipping count"
                  );
                }
              }
            });
          }
        });
      }

      setKeyRequestCounts(counts);
    } catch (error) {
      console.error("Error counting requests per key:", error);
    }
  }, []);

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const keys = getEnvKeys();
      setEnvKeys(keys);
      await countRequestsPerKey(keys);
      setLoading(false);
    } catch (error) {
      console.error("Error loading data:", error);
      showToast("Lỗi khi tải dữ liệu", "error");
      setLoading(false);
    }
  }, [getEnvKeys, countRequestsPerKey]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Mask API key (show only first 4 and last 4 characters)
  const maskApiKey = (key) => {
    if (!key || key.length < 12) return key;
    return `${key.slice(0, 4)}...${key.slice(-4)}`;
  };

  return (
    <div className="admin-content">
      {/* Toast */}
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
          </div>
        </div>
      )}

      <div className="content-header">
        <div>
          <h2>🔑 Quản lý Manus API Keys</h2>
          <p>
            Danh sách các Manus API keys được cấu hình từ environment variables
          </p>
        </div>
      </div>

      {loading ? (
        <div className="empty-state">
          <div className="empty-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </div>
          <p>Đang tải dữ liệu...</p>
        </div>
      ) : (
        <>
          {/* Overall Stats */}
          <div className="data-table">
            <div className="table-header">
              <div className="table-title">
                <h3>📊 Thống kê chung</h3>
              </div>
            </div>
            <div className="table-content" style={{ padding: "1.5rem" }}>
              <div className="stats-grid">
                <div className="stat-card users-card">
                  <div className="stat-card-header">
                    <div className="stat-card-info">
                      <p className="stat-card-title">Tổng Slots</p>
                      <p className="stat-card-value">{envKeys.length}</p>
                    </div>
                  </div>
                </div>
                <div className="stat-card active-users-card">
                  <div className="stat-card-header">
                    <div className="stat-card-info">
                      <p className="stat-card-title">Keys đã cấu hình</p>
                      <p className="stat-card-value">
                        {envKeys.filter((k) => !k.isEmpty).length}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="stat-card transactions-card">
                  <div className="stat-card-header">
                    <div className="stat-card-info">
                      <p className="stat-card-title">Slots trống</p>
                      <p className="stat-card-value">
                        {envKeys.filter((k) => k.isEmpty).length}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="stat-card revenue-card">
                  <div className="stat-card-header">
                    <div className="stat-card-info">
                      <p className="stat-card-title">Tổng requests</p>
                      <p className="stat-card-value">
                        {Object.values(keyRequestCounts).reduce(
                          (a, b) => a + b,
                          0
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* API Keys List with Request Counts */}
          <div className="data-table">
            <div className="table-header">
              <div className="table-title">
                <h3>🔐 Danh sách API Keys</h3>
              </div>
            </div>
            <div className="table-content">
              {envKeys.length === 0 ? (
                <div className="empty-state">
                  <p>
                    Chưa cấu hình Manus API key nào trong environment variables
                  </p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="modern-table">
                    <thead>
                      <tr>
                        <th>Loại</th>
                        <th>Environment Variable</th>
                        <th>API Key</th>
                        <th>Số requests</th>
                        <th>Tỷ lệ sử dụng</th>
                      </tr>
                    </thead>
                    <tbody>
                      {envKeys.map((key) => {
                        const requestCount = keyRequestCounts[key.id] || 0;
                        const totalRequests = Object.values(
                          keyRequestCounts
                        ).reduce((a, b) => a + b, 0);
                        const percentage =
                          totalRequests > 0
                            ? ((requestCount / totalRequests) * 100).toFixed(1)
                            : 0;

                        return (
                          <tr
                            key={key.id}
                            className="table-row-hover"
                            style={{
                              opacity: key.isEmpty ? 0.5 : 1,
                              backgroundColor: key.isEmpty ? "#fafafa" : "transparent"
                            }}
                          >
                            <td>
                              <span
                                className={`status-badge ${
                                  key.isEmpty
                                    ? "inactive"
                                    : key.type === "primary"
                                    ? "success"
                                    : "warning"
                                }`}
                              >
                                {key.isEmpty
                                  ? "⚪ Trống"
                                  : key.type === "primary"
                                  ? "⭐ Primary"
                                  : "🔄 Optional"}
                              </span>
                            </td>
                            <td>
                              <code
                                style={{
                                  fontSize: "12px",
                                  background: "#f5f5f5",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                }}
                              >
                                {key.envVar}
                              </code>
                            </td>
                            <td>
                              {key.isEmpty ? (
                                <span
                                  style={{
                                    fontSize: "13px",
                                    color: "#999",
                                    fontStyle: "italic",
                                  }}
                                >
                                  (chưa cấu hình)
                                </span>
                              ) : (
                                <code
                                  style={{
                                    fontSize: "13px",
                                    background: "#f5f5f5",
                                    padding: "6px 10px",
                                    borderRadius: "4px",
                                    fontWeight: "600",
                                    letterSpacing: "0.5px",
                                  }}
                                  title={key.key}
                                >
                                  {maskApiKey(key.key)}
                                </code>
                              )}
                            </td>
                            <td
                              style={{ fontWeight: "600", textAlign: "center" }}
                            >
                              {key.isEmpty ? "-" : requestCount}
                            </td>
                            <td>
                              {key.isEmpty ? (
                                <span
                                  style={{
                                    fontSize: "12px",
                                    color: "#999",
                                    textAlign: "center",
                                    display: "block",
                                  }}
                                >
                                  -
                                </span>
                              ) : (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  <div
                                    style={{
                                      flex: 1,
                                      height: "8px",
                                      backgroundColor: "#e0e0e0",
                                      borderRadius: "4px",
                                      overflow: "hidden",
                                      minWidth: "60px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        height: "100%",
                                        backgroundColor:
                                          key.type === "primary"
                                            ? "#4CAF50"
                                            : "#2196F3",
                                        width: `${percentage}%`,
                                        transition: "width 0.3s ease",
                                      }}
                                    ></div>
                                  </div>
                                  <span
                                    style={{
                                      fontSize: "12px",
                                      fontWeight: "600",
                                      minWidth: "35px",
                                      textAlign: "right",
                                    }}
                                  >
                                    {percentage}%
                                  </span>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Information */}
          <div className="data-table">
            <div className="table-header">
              <div className="table-title">
                <h3>ℹ️ Thông tin</h3>
              </div>
            </div>
            <div
              className="table-content"
              style={{ padding: "1.5rem", background: "#f8f9fa" }}
            >
              <p style={{ fontSize: "14px", marginBottom: "10px" }}>
                <strong>Cách cấu hình:</strong>
              </p>
              <ul
                style={{
                  fontSize: "14px",
                  paddingLeft: "20px",
                  lineHeight: "1.8",
                }}
              >
                <li>
                  <strong>Primary Key:</strong> Được cấu hình qua{" "}
                  <code>REACT_APP_MANUS_API_KEY</code> (available trong browser và server)
                </li>
                <li>
                  <strong>Optional Keys:</strong> Được cấu hình qua{" "}
                  <code>MANUS_API_KEY_1</code> đến{" "}
                  <code>MANUS_API_KEY_20</code> (không có prefix REACT_APP_)
                </li>
                <li>
                  <strong>Lưu ý:</strong> Các key <code>MANUS_API_KEY_*</code>{" "}
                  chỉ available trong server-side (Vercel functions), không thể đọc được trong browser
                </li>
                <li>
                  Mỗi key được hiển thị với định dạng:{" "}
                  <strong>4 ký tự đầu...4 ký tự cuối</strong>
                </li>
                <li>
                  Cột "Số requests" hiển thị số lượng request định giá đã sử
                  dụng key này (đọc từ Firebase histories)
                </li>
                <li>
                  Cột "Tỷ lệ sử dụng" hiển thị phần trăm request của key so với
                  tổng
                </li>
                <li>
                  Các slot trống sẽ hiển thị với badge "⚪ Trống" và opacity giảm
                </li>
              </ul>

              <p
                style={{
                  fontSize: "14px",
                  marginTop: "15px",
                  marginBottom: "10px",
                }}
              >
                <strong>Ví dụ cấu hình trong Vercel:</strong>
              </p>
              <pre
                style={{
                  background: "#fff",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  padding: "10px",
                  fontSize: "12px",
                  overflow: "auto",
                }}
              >
                {`# Primary key (browser + server)
REACT_APP_MANUS_API_KEY=sk-xxxxx

# Optional keys (server-only)
MANUS_API_KEY_1=sk-yyyyy
MANUS_API_KEY_2=sk-zzzzz
MANUS_API_KEY_3=sk-aaaaa
...
MANUS_API_KEY_20=sk-bbbbb`}
              </pre>

              <div
                style={{
                  marginTop: "15px",
                  padding: "10px",
                  background: "#fff3cd",
                  border: "1px solid #ffc107",
                  borderRadius: "4px",
                  fontSize: "13px",
                }}
              >
                <strong>⚠️ Quan trọng:</strong> Màn hình này chỉ hiển thị keys được set trong environment variables.
                Các key <code>MANUS_API_KEY_*</code> sẽ hiển thị trống khi xem từ browser,
                nhưng vẫn hoạt động bình thường trong Vercel serverless functions.
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminManusKeyManager;
